import React, { useEffect, useState } from "react";
import { Loader2, Plus, Trash2, Save, X } from "lucide-react";
import { toast } from "sonner";

import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { useAuth } from "@/context/AuthContext";
import { api, unwrap } from "@/lib/api";

export default function AdminUsers() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/users");
      setUsers(unwrap(r) || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async (u) => {
    try {
      if (u.id) {
        const body = { email: u.email, full_name: u.full_name, role: u.role, is_active: u.is_active };
        if (u.password) body.password = u.password;
        await api.patch(`/admin/users/${u.id}`, body);
      } else {
        await api.post("/admin/users", { email: u.email, full_name: u.full_name, role: u.role, is_active: u.is_active, password: u.password });
      }
      toast.success("User saved");
      setEditing(null);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail?.message || "Save failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this user?")) return;
    try { await api.delete(`/admin/users/${id}`); toast.success("Deleted"); load(); }
    catch (err) { toast.error(err?.response?.data?.detail?.message || "Delete failed"); }
  };

  if (loading) return <div className="grid place-items-center py-16"><Loader2 className="w-6 h-6 animate-spin" /></div>;

  return (
    <div className="space-y-5" data-testid="admin-users">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl font-extrabold tracking-tighter">Team</h1>
          <p className="text-[#4B5563] text-sm mt-1">{users.length} accounts</p>
        </div>
        <Button onClick={() => setEditing({ email: "", full_name: "", role: "operator", is_active: true, password: "" })}
          className="rounded-xl bg-[#0EA5A4] hover:bg-[#0F766E] text-white" data-testid="add-user-btn">
          <Plus className="w-4 h-4 mr-1" /> Add user
        </Button>
      </div>

      <Card className="rounded-2xl border border-[#E5E7EB] overflow-hidden">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-xs uppercase tracking-wider text-[#4B5563]">
            <th className="px-5 py-3">Name</th><th className="px-5 py-3">Email</th><th className="px-5 py-3">Role</th><th className="px-5 py-3">Active</th><th className="px-5 py-3"></th>
          </tr></thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-t border-[#E5E7EB]">
                <td className="px-5 py-3 font-semibold">{u.full_name}</td>
                <td className="px-5 py-3">{u.email}</td>
                <td className="px-5 py-3 capitalize">{u.role}</td>
                <td className="px-5 py-3">{u.is_active ? "Yes" : "No"}</td>
                <td className="px-5 py-3 text-right">
                  <Button size="sm" variant="outline" className="rounded-xl mr-2" onClick={() => setEditing({ ...u, password: "" })} data-testid={`edit-user-${u.email}`}>Edit</Button>
                  <Button size="sm" variant="outline" className="rounded-xl text-red-500" disabled={u.id === user?.id} onClick={() => remove(u.id)} data-testid={`delete-user-${u.email}`}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      {editing && <UserEditor data={editing} onSave={save} onCancel={() => setEditing(null)} />}
    </div>
  );
}

function UserEditor({ data, onSave, onCancel }) {
  const [u, setU] = useState(data);
  const upd = (patch) => setU((s) => ({ ...s, ...patch }));
  return (
    <Card className="rounded-2xl p-6 border border-[#0EA5A4]" data-testid="user-editor">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-display font-bold">{u.id ? "Edit user" : "New user"}</h3>
        <button onClick={onCancel}><X className="w-5 h-5" /></button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div><Label>Full name</Label><Input value={u.full_name} onChange={(e) => upd({ full_name: e.target.value })} className="rounded-xl mt-1" data-testid="user-name" /></div>
        <div><Label>Email</Label><Input type="email" value={u.email} onChange={(e) => upd({ email: e.target.value })} className="rounded-xl mt-1" data-testid="user-email" /></div>
        <div><Label>Role</Label>
          <select value={u.role} onChange={(e) => upd({ role: e.target.value })}
            className="mt-1 w-full h-10 rounded-xl border border-[#E5E7EB] px-3 bg-white" data-testid="user-role">
            <option value="admin">admin</option><option value="operator">operator</option>
          </select>
        </div>
        <div><Label>{u.id ? "New password (leave empty to keep)" : "Password"}</Label>
          <Input type="password" value={u.password || ""} onChange={(e) => upd({ password: e.target.value })} className="rounded-xl mt-1" data-testid="user-password" />
        </div>
        <div className="flex items-center gap-3">
          <Switch checked={u.is_active} onCheckedChange={(v) => upd({ is_active: v })} data-testid="user-active" />
          <Label>Active</Label>
        </div>
      </div>
      <div className="mt-5 flex gap-2">
        <Button onClick={() => onSave(u)} className="rounded-xl bg-[#0EA5A4] hover:bg-[#0F766E] text-white" data-testid="user-save"><Save className="w-4 h-4 mr-2" /> Save</Button>
        <Button onClick={onCancel} variant="outline" className="rounded-xl" data-testid="user-cancel">Cancel</Button>
      </div>
    </Card>
  );
}
